import {
  Component,
  ChangeDetectionStrategy,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
  HostListener,
  ViewEncapsulation,
} from "@angular/core";
import { IrbRebalanceEditor } from "@irb/irb-editors/irb-editors.rebalance.base";
import { IEvent, IrbAppEvents, IrbEditorsEvents } from "@irb/irb-common/events";
import { RebalanceService } from "../rebalance/rebalance.service";
import {
  AnalysisSchedulerDTO,
  IAnalysisAdjustMultiSecsDTO,
  IAnalysisAdjustSecuritiesDTO,
  IAnalysisPackage,
  ISaveRebalanceResponse,
  ISecurityDropDown,
  ISaveRebalanceAsync
} from "../rebalance/irb-rebalance.model";
import { ISmartFilterDto } from "../irb-editors.model";
import { IAlertDialogData } from "@irb/app.model";
import { Observable, of } from "rxjs";
import { IrbDashboardService } from "@irb/irb-dashboard/irb-dashboard.service";
import { IGlobalSettings } from "@irb/irb-dashboard/irb-dashboard.model";
import { RebalanceIncludedPortfolioComponent } from "../rebalance/rebalance-included-portfolio/rebalance-included-portfolio.component";
import { AnalyticsService } from "@irb-common/analytics/analytics.service";

@Component({ standalone: false,
  selector: "irb-editor-adjust-securities-rebalance",
  templateUrl: "./editor-adjust-securities-rebalance.component.html",
  styleUrls: [
    "./editor-adjust-securities-rebalance.component.scss",
    "../../irb-common/styles/dialog.scss",
    "../editor-sidebar/editor-sidebar.component.scss",
  ],
  changeDetection: ChangeDetectionStrategy.Default,
  encapsulation: ViewEncapsulation.None,
})
export class EditorAdjustSecuritiesRebalanceComponent extends IrbRebalanceEditor {
  @ViewChild("body") body: ElementRef;
  @ViewChild("includedportfolio")
  includedportfolio: RebalanceIncludedPortfolioComponent;

  defaultExpandedPanels: Array<string> = [
    "REBALANCE_SETTINGS",
    "INCLUDED_PORTFOLIOS",
    "REBALANCE_SCHEDULER",
  ];
  isReadOnly: boolean = false;
  saveLabel: string = "Process";
  securityDropDownRes: ISecurityDropDown;
  summarySource: string = undefined;
  rebalanceTitle: string;
  analysisName: string = "Adj Securities - ";
  analysisType: string = "Adjust securities";
  isUnitTest: boolean = false;

  constructor(
    private cdr: ChangeDetectorRef,
    private dbs: IrbDashboardService,
    private readonly rebalanceService: RebalanceService,
    public ats: AnalyticsService
  ) {
    super();
    this.identifier = "analysisId";
  }

  get(event: IEvent) {
    super.get(event);
    if (event.data) {
      this.rebalanceService.setRebalanceSource(event.data.source || null);
    }
    let incEqvAndAltSecs: boolean = false;
    this.dbs.getGlobalSettings().subscribe((gsData: IGlobalSettings) => {
      incEqvAndAltSecs = gsData.includeEquivalentAndAltSecurities;
      this.getEventData(event, incEqvAndAltSecs);
    });
  }
  getEventData(event: IEvent, incEqvAndAltSecs: boolean) {
    let eventData: IAnalysisPackage;
    let todayDate = this.datePipe.transform(new Date(), "MM/dd/yyyy");
    if (event.data && event.data.navigationSource === "schedulePage") {
      
      this.ignoreUnsavedChanges = true;
      this.saveLabel = "Schedule";
      this.rebalanceTitle = "Create Adjust Securities";
      this.isReadOnly = false;
      eventData = {
        analysisType: "Adjust securities",
        adjustSecDto: {
          isTaxable: true,
          isQualified: true,
          isCashMin: false,
          isCashMax: false,
          isCashTarget: true,
          includeEquivalentAndAltSecurities: incEqvAndAltSecs,
          adjustMultipleSecurities: [],
        } as IAnalysisAdjustSecuritiesDTO,
        isDeleted: false,
        isUIdeleted: false,
        isNew: true,
        schedulerFrequency: "None",
        navigationSource: event.data.navigationSource,
        smartFilterDto:
          event.data && event.data.smartFilterDto
            ? event.data.smartFilterDto
            : ({
                analysisType: this.analysisType,
                isCategoryAll: false,
                isCategoryBlank: false,
                isModelAll: false,
                isModelBlank: false,
                modelUpdated: false,
                sfCategoryDtoList: null,
                sfModelDtoList: null,
                smartFilterDays: null,
                analysisPortDtoList: [],
              } as ISmartFilterDto),
        scheduler: { scheduleType: "Recurring" },
      } as IAnalysisPackage;

      this.applyRebalanceData(event, eventData, todayDate);
      let expandedPanels = this.getDefaultExpandedPanels();
      this.ies.loadExpansionState(
        this.defaultExpandedPanels.length,
        "editor-adjust-securities-rebalance",
        expandedPanels,
        false
      );
      this.cdr.detectChanges();
      this.onResize();
    } else {
      if (
        event.data !== null &&
        event.data !== undefined &&
        event.data.analysisId !== null &&
        event.data.analysisId !== undefined
      ) {
        eventData = event.data;
        this.summarySource = event.data.source;
        if (
          this.summarySource !== null &&
          this.summarySource !== undefined &&
          (this.summarySource === "resultSummary" ||
            this.summarySource === "portfolioResultDetail")
        ) {
          this.rebalanceService
            .getHistoryDetail(event.data.analysisId)
            .subscribe((data: any) => {
              eventData = data.responseData;
              eventData.adjustSecDto.includeEquivalentAndAltSecurities =
                incEqvAndAltSecs;
              this.load(eventData);
            });
        } else if (
          this.summarySource !== null &&
          this.summarySource !== undefined &&
          (this.summarySource === "Scheduled" ||
            this.summarySource === "portfolios")
        ) {
          this.isReadOnly = false;
          this.rebalanceService
            .getRebalanceDetail(event.data.analysisId)
            .subscribe((data: any) => {
              eventData = data.responseData;
              eventData.adjustSecDto.includeEquivalentAndAltSecurities =
                incEqvAndAltSecs;
              this.load(eventData);
            });
        }
      } else {
        
        this.ignoreUnsavedChanges = true;
        this.rebalanceTitle = "Create Adjust Securities";
        this.isReadOnly = false;
        eventData = {
          analysisType: "Adjust securities",
          isSysSuggestion: true,
          isUIAllPort: false,
          analysisName: this.analysisName + todayDate,
          tradeExpln: "",
          adjustSecDto: {
            isTaxable: true,
            isQualified: true,
            isCashMin: false,
            isCashMax: false,
            isCashTarget: true,
            includeEquivalentAndAltSecurities: incEqvAndAltSecs,
            adjustMultipleSecurities: [],
          } as IAnalysisAdjustSecuritiesDTO,
          isDeleted: false,
          isUIdeleted: false,
          isNew: true,
          schedulerFrequency: "None",
          smartFilterDto:
            event.data && event.data.smartFilterDto
              ? event.data.smartFilterDto
              : ({
                  analysisType: this.analysisType,
                  isCategoryAll: false,
                  isCategoryBlank: false,
                  isModelAll: false,
                  isModelBlank: false,
                  modelUpdated: false,
                  sfCategoryDtoList: null,
                  sfModelDtoList: null,
                  smartFilterDays: null,
                  analysisPortDtoList: [],
                } as ISmartFilterDto),
          scheduler: { scheduleType: "Adhoc" },
        } as IAnalysisPackage;

        if (event.data && event.data.modelId) {
          eventData.modelId = event.data.modelId;
          eventData.modelName = event.data.modelName;
        }

        this.applyRebalanceData(event, eventData, todayDate);
        let expandedPanels = this.getDefaultExpandedPanels();
        this.ies.loadExpansionState(
          this.defaultExpandedPanels.length,
          "editor-adjust-securities-rebalance",
          expandedPanels,
          false
        );
        this.cdr.detectChanges();
        this.onResize();
      }
      if (
        this.summarySource !== null &&
        this.summarySource !== undefined &&
        (this.summarySource === "resultSummary" ||
          this.summarySource === "portfolioResultDetail")
      ) {
        this.isReadOnly = true;
        this.rebalanceTitle = "View Adjust Securities";
      } else if (
        this.summarySource !== null &&
        this.summarySource !== undefined &&
        (this.summarySource === "Scheduled" ||
          this.summarySource === "portfolios")
      ) {
        this.ats.trackScheduledRebalancesEditEvent();
        this.isReadOnly = false;
        this.saveLabel = "Schedule";
        this.rebalanceTitle = "Edit Adjust Securities";
      } else {
        
        this.isReadOnly = false;
        this.saveLabel = "Process";
        if (eventData.adjustSecDto) {
          eventData.adjustSecDto.includeEquivalentAndAltSecurities =
            incEqvAndAltSecs;
        }
        this.rebalanceTitle = "Create Adjust Securities";
      }
      if (
        event.data.source !== null &&
        event.data.source !== undefined &&
        event.data.source === "rebalancePortfolioDialog"
      ) {
        if (
          this.data.smartFilterDto.analysisPortDtoList !== null &&
          this.data.smartFilterDto.analysisPortDtoList !== undefined
        ) {
          this.isReadOnly = false;
          this.isModified = true;
          this.originalData.summarySource = "rebalancePortfolioDialog";
          this.data.tradeExpln = undefined;
          this.cdr.detectChanges();
        }
      }
    }
  }

  load(data: AnalysisSchedulerDTO) {
    super.load(data);
    let expandedPanels = this.getDefaultExpandedPanels();
    this.ies.loadExpansionState(
      this.defaultExpandedPanels.length,
      "editor-adjust-securities-rebalance",
      expandedPanels,
      false
    );
    this.cdr.detectChanges();
    this.onResize();
  }

  saveReplaceSecuritiesRebalance() {
    this.as.fireEvent(IrbEditorsEvents.SAVE_START, { slot: this.slot });
    const includePortfolioList = this.includedportfolio.getRowData();
    this.data.smartFilterDto.analysisPortDtoList = includePortfolioList;
    this.data.smartFilterDto.analysisPortDtoListCount = includePortfolioList.length;
    this.validateInput().subscribe((errors) => {
      if (errors.length > 0) {
        let errMsg: string = errors.join("\n");

        this.as.fireEvent(IrbAppEvents.SHOW_ALERT, {
          dTitle: "Alert",
          msg: errMsg,
          dProceedBtnLabel: "Ok",
          dType: "ERROR",
        } as IAlertDialogData);

        this.as.fireEvent(IrbEditorsEvents.HIDE_WAIT, { slot: this.slot });
      } else {
        if (this.data.schedulerFrequency === "Daily") {
          this.data.schedulerFrequency = this.data.scheduler.reccuringFreqType;
        }
        delete this.data.navigationSource;
        this.trackEditEvent(this.data);
        let a = this.data.smartFilterDto.analysisPortDtoList.length >= this.portfolioThreshold;
        let b = this.data.adjustSecDto.adjustMultipleSecurities.length >= this.securityThreshold;

        if(a || b) {
          this.crbservice.saveRebalanceAsync(this.data).subscribe((data: ISaveRebalanceAsync) => {
            this.checkSaveRebalanceAsyncStatus(data.rprasId);
          }, () => {
            this.as.fireEvent(IrbEditorsEvents.SAVE_ERROR, { "slot": this.slot });
            this.setGlobalError();
          });
        } else {
          this.rebalanceService.saveRebalance(this.data).subscribe((data: ISaveRebalanceResponse) => {
              this.completeSaveRebalance(data);
            },
            () => {
              this.as.fireEvent(IrbEditorsEvents.SAVE_ERROR, { slot: this.slot });
              this.setGlobalError();
          });
        }
      }
    });
  }

  completeSaveRebalance(response: ISaveRebalanceResponse) {
            if (response.responseStatus === "SUCCESS") {
              if (response.responseUtilityMap != null) {
                let analysisId = String(response.responseUtilityMap.analysisId);
                let rebalanceResultId = String(
                  response.responseUtilityMap.rebalanceResultId
                );
                let rebalanceName = response.responseUtilityMap.rebalanceName;

                let submitTiQueueReq: any = {
                  Package: analysisId,
                  RebalanceResult: rebalanceResultId,
                };

                let saveSuccessEventData: {};
                let hasPendFunc: boolean = this.hasPendingFunction;
                if (!hasPendFunc) {
                  saveSuccessEventData = {
                    hasPendingFunction: hasPendFunc,
                    slot: this.slot,
                  };
                  if (this.data.schedulerFrequency === "None") {
                    this.submitPackageToQueue(
                      submitTiQueueReq,
                      rebalanceName,
                      rebalanceResultId,
                      hasPendFunc,
                      saveSuccessEventData
                    );
                  } else {
                    this.navigateToScheduledRebalances();
                  }
                } else {
                  saveSuccessEventData = {
                    hasPendingFunction: hasPendFunc,
                    data: rebalanceName + " Rebalance Submitted",
                    slot: this.slot,
                  };
                  if (this.data.schedulerFrequency === "None") {
                    this.submitPackageToQueue(
                      submitTiQueueReq,
                      rebalanceName,
                      rebalanceResultId,
                      hasPendFunc,
                      saveSuccessEventData
                    );
                  }
                }
              }
            } else {
              this.as.fireEvent(IrbEditorsEvents.SAVE_ERROR, {
                slot: this.slot,
              });
              this.setGlobalError();
            }
  }

  submitPackageToQueue(submitTiQueueReq: any, rebalanceName: string, rebalanceResultId: any, hasPendFunc: boolean,saveSuccessEventData:any) {
    this.rebalanceService.submitPortfoliosToQueue(submitTiQueueReq).subscribe(() => {
      if (!hasPendFunc) {
        this.alertUserOnSubmissionOrSave(rebalanceName, rebalanceResultId,saveSuccessEventData);
      } else {
        setTimeout(() => { this.as.fireEvent(IrbEditorsEvents.SAVE_SUCCESS, saveSuccessEventData); });
      }
    });    
  }

  alertUserOnSubmissionOrSave(rebalanceName: string, rebalanceResultId: any, saveSuccessEventData: any) {
    this.as.fireEvent(IrbAppEvents.SHOW_ALERT, {
      dTitle: "Rebalance Submitted",
      msg: rebalanceName,
      altMsg:
        "Go to rebalance results summary to check the status of this submission.",
      dProceedBtnLabel: "Go to Rebalance Result Summary",
      dType: "WARN",
      onProceed: (() => {
        this.navigateToResultSummary(rebalanceResultId);
        this.closeWithoutSave();
      }).bind(this),
      onCancel: (() => {
        this.closeWithoutSave();
      }).bind(this),
    } as IAlertDialogData);
    
    setTimeout(() => { this.as.fireEvent(IrbEditorsEvents.SAVE_SUCCESS, saveSuccessEventData); });
  }

  validateInput(): Observable<string[]> {
    let errors: string[] = [];
    const adjustSecDto: IAnalysisAdjustSecuritiesDTO = this.data.adjustSecDto;
    
    if (adjustSecDto) {
      if (!adjustSecDto.isTaxable && !adjustSecDto.isQualified) {
        errors.push(
          "Select either taxable accounts, qualified accounts, or both to submit this rebalance."
        );
      }
      if (
        adjustSecDto.adjustMultipleSecurities === null ||
        adjustSecDto.adjustMultipleSecurities === undefined ||
        adjustSecDto.adjustMultipleSecurities.length < 1
      ) {
        if (!this.data.isUIAllPort) {
          errors.push("Add at least one security to this rebalance.");
        }
      } else {
        adjustSecDto.adjustMultipleSecurities.forEach(
          (element: IAnalysisAdjustMultiSecsDTO) => {
            if (
              element.transactionType === null ||
              element.transactionType === undefined
            ) {
              errors.push(
                "Security " +
                  element.securitySymb +
                  ", requires a value in Order Type column"
              );
            }
             const percentRequiredTypes = ['ADD_PREC', 'REDUCE_PREC', 'SPECIFY_PERC'];
            if (
              element.targetType !== null &&
              element.targetType !== undefined &&
              percentRequiredTypes.includes(element.targetType) &&
              (element.targetPercent === null || element.targetPercent === undefined)
            ) {
              errors.push(
                "Security " + element.securitySymb +
                ", % of portfolio is required when Target is Add %, Reduce %, or Specify %."
              );
            }
            if (
              element.targetType === null ||
              element.targetType === undefined
            ) {
              errors.push(
                "Security " +
                  element.securitySymb +
                  ", requires a value in Target Type column"
              );
            }
            if (
              element.targetType !== null &&
              element.targetType !== undefined &&
              element.targetType !== "MODEL_PERC" &&
              element.targetPercent !== null &&
              element.targetPercent !== undefined &&
              (element.targetPercent < 0 || element.targetPercent > 100)
            ) {
              errors.push(
                "Security " +
                  element.securitySymb +
                  ', a value is needed in the "% of Portfolio" between 0-100.'
              );
            }
          }
        );
      }
    }

    errors = errors.concat(this.validateDataInput());
    return of(errors);
  }

  @HostListener("window:resize", ["$event"])
  onResize() {
    if (this.body) {
      const rect = this.body.nativeElement.getBoundingClientRect();
      let h = window.innerHeight - rect.top - 10;

      h = h < 200 ? 200 : h;
      this.bodyHeight = h + "px";
    }
  }

  close() {
    super.close();

    if (this.isModified) {
      this.as.fireEvent(IrbAppEvents.GRID_REFRESH);
    }
  }

  getDefaultExpandedPanels() {
    let defaultExpandedPanels: Array<string> = [
      "REBALANCE_SETTINGS",
      "INCLUDED_PORTFOLIOS",
      "REBALANCE_SCHEDULER",
    ];
    // QC 168261
    // let source = this.rebalanceService.getRebalanceSource();
    // if(source!==undefined && source !== null && source !=='' && source.length > 0) {
    //     defaultExpandedPanels = ["REBALANCE_SETTINGS", "REBALANCE_SCHEDULER"];
    // }
    return defaultExpandedPanels;
  }
  getPortfolioCount() {
    if (this.data && this.data.isUIAllPort && this.data.isUIAllPort == true) {
      return "All portfolios included";
    }
    if (
      this.data?.smartFilterDto?.analysisPortDtoList !== null &&
      this.data?.smartFilterDto?.analysisPortDtoList !== undefined &&
      this.data?.smartFilterDto?.analysisPortDtoList?.length
    ) {
      if (this.data?.smartFilterDto?.analysisPortDtoList?.length > 1) {
        return (
          this.data?.smartFilterDto?.analysisPortDtoList?.length +
          " portfolios included"
        );
      } else if (this.data?.smartFilterDto?.analysisPortDtoList?.length === 1) {
        return (
          this.data?.smartFilterDto?.analysisPortDtoList?.length +
          " portfolio included"
        );
      } else {
        return 0 + " portfolios included";
      }
    } else {
      return 0 + " portfolios included";
    }
  }
}
